// src/components/UpdateDetectionDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  CloudDownload, 
  AddCircleOutline, 
  Refresh, 
  DeleteOutline 
} from '@mui/icons-material';

const UpdateDetectionDialog = ({ 
  open, 
  onClose, 
  updates = [], 
  loading = false,
  onApplyUpdates 
}) => {
  // Count updates by type
  const getUpdateCounts = () => {
    return updates.reduce((counts, update) => {
      counts[update.type] = (counts[update.type] || 0) + 1;
      return counts;
    }, {});
  };
  
  const updateCounts = getUpdateCounts();
  
  // Get icon for update type
  const getUpdateIcon = (type) => {
    switch (type) {
      case 'new':
        return <AddCircleOutline color="success" />;
      case 'updated':
        return <Refresh color="primary" />;
      case 'deleted':
        return <DeleteOutline color="error" />;
      default:
        return <CloudDownload />;
    }
  };
  
  // Get color for update type
  const getUpdateColor = (type) => {
    switch (type) {
      case 'new':
        return 'success.main';
      case 'updated':
        return 'primary.main';
      case 'deleted':
        return 'error.main';
      default:
        return 'text.primary';
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Document Updates Available
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ mr: 2 }} />
            <Typography variant="body1">
              Checking for document updates...
            </Typography>
          </Box>
        ) : updates.length === 0 ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" align="center">
              All documents are up to date.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              {updates.length} document {updates.length === 1 ? 'update' : 'updates'} found.
              Would you like to download these updates for offline use?
            </Typography>
            
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              {updateCounts.new > 0 && (
                <Box sx={{ 
                  bgcolor: 'success.light', 
                  color: 'success.dark',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <AddCircleOutline fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {updateCounts.new} New
                  </Typography>
                </Box>
              )}
              
              {updateCounts.updated > 0 && (
                <Box sx={{ 
                  bgcolor: 'primary.light', 
                  color: 'primary.dark',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Refresh fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {updateCounts.updated} Updated
                  </Typography>
                </Box>
              )}
              
              {updateCounts.deleted > 0 && (
                <Box sx={{ 
                  bgcolor: 'error.light', 
                  color: 'error.dark',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {updateCounts.deleted} Removed
                  </Typography>
                </Box>
              )}
            </Box>
            
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, mb: 2 }}>
              {updates.slice(0, 5).map((update, index) => (
                <React.Fragment key={update.document.id}>
                  <ListItem>
                    <ListItemIcon>
                      {getUpdateIcon(update.type)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={update.document.name} 
                      secondary={update.section}
                      primaryTypographyProps={{ 
                        color: getUpdateColor(update.type)
                      }}
                    />
                  </ListItem>
                  {index < Math.min(updates.length, 5) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            
            {updates.length > 5 && (
              <Typography variant="body2" color="text.secondary" align="center">
                ...and {updates.length - 5} more updates
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        {loading ? (
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
        ) : updates.length === 0 ? (
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Later
            </Button>
            <Button 
              onClick={onApplyUpdates}
              color="primary" 
              variant="contained"
              startIcon={<CloudDownload />}
            >
              Download Updates
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDetectionDialog;